/**
 * Conversations API - RTK Query
 *
 * Email-based messaging system endpoints
 */

import { baseApi } from "./baseApi";

// Types
export interface Message {
	id: string;
	conversation: string;
	sender: string;
	sender_name: string;
	content: string;
	is_read: string[];
	email_sent: string | null;
	created_at: string;
}

export interface Conversation {
	id: string;
	organization: string;
	participants: string[];
	participant_names: string[];
	subject: string;
	related_property?: string | null;
	related_lease?: string | null;
	last_message_at: string | null;
	created_at: string;
	messages?: Message[];
}

export interface ConversationListItem {
	id: string;
	subject: string;
	participant_count: number;
	last_message_at: string | null;
	created_at: string;
}

export interface CreateConversationRequest {
	organization?: string; // Optional - backend will auto-assign from user if not provided
	participants: string[];
	subject: string;
	related_property?: string;
	related_lease?: string;
}

export interface SendMessageRequest {
	content: string;
}

export interface ConversationsListResponse {
	results?: ConversationListItem[];
	count?: number;
}

/**
 * Conversations API endpoints
 */
export const conversationsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		// Get all conversations for current user
		getConversations: builder.query<ConversationListItem[], void>({
			query: () => ({
				url: "/conversations/",
				method: "GET",
			}),
			transformResponse: (
				response: ConversationListItem[] | ConversationsListResponse,
			) => {
				// Handle both array and object response formats
				if (Array.isArray(response)) {
					return response;
				}
				return response.results || [];
			},
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: "Conversations" as const,
								id,
							})),
							{ type: "Conversations", id: "LIST" },
					  ]
					: [{ type: "Conversations", id: "LIST" }],
		}),

		// Get conversation by ID with all messages
		getConversationById: builder.query<Conversation, string>({
			query: (id) => ({
				url: `/conversations/${id}/`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "Conversations", id }],
			// Don't retry on 404 - conversation doesn't exist
			extraOptions: {
				maxRetries: 0,
			},
		}),

		// Create new conversation
		createConversation: builder.mutation<
			Conversation,
			CreateConversationRequest
		>({
			query: (data) => ({
				url: "/conversations/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: [{ type: "Conversations", id: "LIST" }],
		}),

		// Send message in conversation
		sendMessage: builder.mutation<
			Message,
			{ conversationId: string; data: SendMessageRequest }
		>({
			query: ({ conversationId, data }) => ({
				url: `/conversations/${conversationId}/send_message/`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: (result, error, { conversationId }) => [
				{ type: "Conversations", id: conversationId },
				{ type: "Conversations", id: "LIST" },
			],
		}),
	}),
});

// Export hooks
export const {
	useGetConversationsQuery,
	useGetConversationByIdQuery,
	useCreateConversationMutation,
	useSendMessageMutation,
	useLazyGetConversationsQuery,
	useLazyGetConversationByIdQuery,
} = conversationsApi;
